import { useState } from 'react';

import type { CompanyApi } from '@/modules/companies/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/modules/ui/components/dialog/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import type { UserApi } from '@/modules/users/types';

interface AssignTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserApi | null;
  availableCompanies: CompanyApi[] | undefined;
  onSubmit: (userId: string, tenantId: string) => void;
  isPending: boolean;
}

export const AssignTenantDialog = ({
  open,
  onOpenChange,
  user,
  availableCompanies,
  onSubmit,
  isPending,
}: AssignTenantDialogProps) => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(
    undefined,
  );

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedTenantId(undefined);
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = () => {
    if (user && selectedTenantId) {
      onSubmit(user.id, selectedTenantId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Assign Tenant to {user?.firstName} {user?.lastName}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Select onValueChange={setSelectedTenantId} value={selectedTenantId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a tenant" />
            </SelectTrigger>
            <SelectContent>
              {availableCompanies?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedTenantId}
            loading={isPending}
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
